import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useRef, useState} from 'react'

// Types for the viewer settings
export interface ViewerSettings {
  emojiTone: number
  pasteUrlsAsPlainText: boolean
  useMonospaceFont: boolean
}

// Default values fallback
const DEFAULT_VIEWER_SETTINGS: ViewerSettings = {
  emojiTone: 0,
  pasteUrlsAsPlainText: false,
  useMonospaceFont: false,
}

// Hook return type
interface UseViewerSettingsReturn {
  settings: ViewerSettings
  loading: boolean
  error: boolean
}

// Global cache to prevent duplicate requests
const globalCache = new Map<string, Promise<ViewerSettings>>()

// Storage key for local storage
const STORAGE_KEY = 'user.preferences.viewer-settings'

// Endpoint URL
const VIEWER_SETTINGS_ENDPOINT = '/settings/appearance/viewer-settings'

/**
 * Validates and extracts viewer settings from a response payload
 */
function extractViewerSettings(payload: unknown): ViewerSettings | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>

  // Validate and extract expected fields
  const emojiTone = typeof data.emojiTone === 'number' ? data.emojiTone : DEFAULT_VIEWER_SETTINGS.emojiTone
  const pasteUrlsAsPlainText =
    typeof data.pasteUrlsAsPlainText === 'boolean'
      ? data.pasteUrlsAsPlainText
      : DEFAULT_VIEWER_SETTINGS.pasteUrlsAsPlainText
  const useMonospaceFont =
    typeof data.useMonospaceFont === 'boolean' ? data.useMonospaceFont : DEFAULT_VIEWER_SETTINGS.useMonospaceFont

  // Check if any of the expected fields are present and valid
  if (data.emojiTone !== undefined || data.pasteUrlsAsPlainText !== undefined || data.useMonospaceFont !== undefined) {
    return {
      emojiTone,
      pasteUrlsAsPlainText,
      useMonospaceFont,
    }
  }

  return null
}

/**
 * Fetches viewer settings from the server
 */
async function fetchViewerSettings(): Promise<ViewerSettings> {
  try {
    const response = await reactFetchJSON(VIEWER_SETTINGS_ENDPOINT)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()
    const extractedSettings = extractViewerSettings(data)

    if (!extractedSettings) {
      throw new Error('Invalid response payload')
    }

    return extractedSettings
  } catch (error) {
    throw error
  }
}

/**
 * Hook to retrieve viewer settings with caching and local storage persistence
 *
 * Features:
 * - Makes only one network request for the entire app lifetime
 * - Persists settings in local storage
 * - Returns cached data on subsequent calls
 * - Falls back to cached data or defaults on network failures
 */

// Allow injecting a custom useRef for testing
export function useViewerSettings(clearGlobalCache = false): UseViewerSettingsReturn {
  const [cachedSettings, setCachedSettings] = useLocalStorage<ViewerSettings | null>(STORAGE_KEY, null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const hasFetchedRef = useRef(false)

  if (clearGlobalCache) {
    // Clear the global cache if requested (for testing purposes)
    globalCache.clear()
    hasFetchedRef.current = false
  }

  const fetchData = useCallback(async () => {
    // Check if we already have a request in flight globally
    let fetchPromise = globalCache.get(VIEWER_SETTINGS_ENDPOINT)

    try {
      if (!fetchPromise) {
        // Create new request and cache it globally
        fetchPromise = fetchViewerSettings()
        globalCache.set(VIEWER_SETTINGS_ENDPOINT, fetchPromise)
      }

      const settings = await fetchPromise
      setCachedSettings(settings)
      setError(false)
    } catch {
      setError(true)
      // If fetch fails and we don't have cached data, keep error state
      // but return cached data if available (handled in the return logic)
    } finally {
      setLoading(false)
    }
  }, [setCachedSettings])

  useEffect(() => {
    // Only fetch once per app lifetime
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchData()
    } else {
      // If we've already fetched, just set loading to false
      setLoading(false)
    }
  }, [fetchData])

  const finalSettings = cachedSettings || DEFAULT_VIEWER_SETTINGS

  return {
    settings: finalSettings,
    loading,
    error: error && !cachedSettings, // Only show error if no cached data available
  }
}
